/**
 * approvalDocToItem — ONE mapping from a `clients/{id}/pendingApprovals` document to
 * the item the Control Center's preview pop-up (ApprovalPreviewModal) draws.
 *
 * 🔴 WHY THIS MOVED OUT OF Approvals.tsx (2026-10-27, CPA Jake on his call with John:
 * "the Google post for Nov 3 shows nothing on Preview, and some others too").
 *
 * The mapping lived inline in the page, so nothing could render it in a test. Measured on
 * his account that day: his `gbp-post` stores its words as `postText` and its picture as
 * `postPhotoUrl`, and carries NO platform. The page read `text` / `imageUrl` / `platforms`,
 * so the item had no platform → the pop-up picked none of its Facebook / Instagram / Google
 * mockups → BLANK. Every gbp-post on file (15, four clients) was blank the same way, and the
 * types with no dedicated card (skill sign-off, site change, new website, link audit,
 * texting invite, quarterly plan, Gmail send, profile art, the retired listing-questions type) fell into the social
 * branch with no platform and drew nothing either.
 *
 * The class is the one approvalContentFields.ts already names: writer and reader disagree
 * about a field, absence renders as nothing, and nothing compares them. The gate is
 * `__tests__/approvalPreviewCensus.test.ts`: every PendingApprovalType (from the backend's
 * own APPROVAL_PAGE_VIEWS table) has a real stored shape in
 * `__fixtures__/approvalPreviewShapes.json`, and each one is mapped HERE and rendered by
 * the real pop-up; an empty render fails.
 */
import type { ApprovalItem } from '../components/ui/ApprovalPreviewModal'
import type { PendingApprovalDoc } from '../hooks/useClientData'
import {
  readApprovalContent,
  readApprovalPlatforms,
  readApprovalRating,
  readApprovalVideoDuration,
} from './approvalContentFields'
import { blogHtmlToMarkdown } from './blogBody'

export type ApprovalFilter = 'all' | 'social' | 'video' | 'ad' | 'review-response' | 'nurture' | 'web' | 'other'

export const AGENT_TYPE_TO_NAME: Record<string, string> = {
  social: 'Social Skill', video: 'Video Skill', ads: 'Ads Skill',
  google: 'Google Skill', nurture: 'Nurture Skill', web: 'Web Skill',
  phone: 'Phone Skill',
}

export const AGENT_NAME_TO_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(AGENT_TYPE_TO_NAME).map(([k, v]) => [v, k]),
)

const DELIVERABLE_TO_FILTER: Record<string, ApprovalFilter> = {
  'facebook-post': 'social', 'instagram-post': 'social', 'gbp-post': 'social',
  'youtube-video': 'video', 'youtube-short': 'video',
  'facebook-ad': 'ad', 'instagram-ad': 'ad', 'google-lsa': 'ad',
  'review-response': 'review-response',
  'sms-nurture': 'nurture', 'email-nurture': 'nurture',
}

/** Map a stored approval type to its card family. */
export function contentTypeToFilter(t: string | undefined): ApprovalFilter {
  const v = (t || '').toLowerCase()
  if (DELIVERABLE_TO_FILTER[v]) return DELIVERABLE_TO_FILTER[v]
  if (/(^|-)ad(-|$)|ad-creative/.test(v)) return 'ad'
  if (/review/.test(v)) return 'review-response'
  if (/nurture|email|sms/.test(v)) return 'nurture'
  if (/video|reel|short|clip|interview/.test(v)) return 'video'
  return 'social'
}

/**
 * Types with no social / video / ad / reply / nurture / blog card of their own. Each is
 * drawn as a SUMMARY: its own words, its list, and the link it is about. The retired Story
 * pieces (demo fixture only) are listed so they say what they are instead of showing a
 * blank ad or a "no video file" box.
 */
const SUMMARY_TYPES = new Set([
  'skill-signoff', 'site-change', 'website', 'site-link-audit', 'opt-in-invite-campaign',
  'quarterly-promo-plan', 'connected-account-send', 'social-profile', 'gbp-qa',
  'ad-creative', 'blog-transcription', 'facebook-reel', 'full-interview', 'gbp-video',
  'instagram-reel', 'quote-graphic', 'topic-clip', 'website-embed',
])

/** Types that are a Google Business Profile piece even when no platform is stored. */
const GOOGLE_TYPES = new Set(['gbp-post'])

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined)
const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0) : []

/** "Tue, Nov 3" from a Firestore Timestamp / {seconds} / ISO string, else undefined. */
export function formatApprovalDate(v: unknown, timeZone?: string): string | undefined {
  let ms: number | undefined
  if (v && typeof v === 'object') {
    const o = v as { toMillis?: () => number; seconds?: number; _seconds?: number }
    if (typeof o.toMillis === 'function') ms = o.toMillis()
    else if (typeof o.seconds === 'number') ms = o.seconds * 1000
    else if (typeof o._seconds === 'number') ms = o._seconds * 1000
  } else if (typeof v === 'string') {
    const t = Date.parse(v)
    if (Number.isFinite(t)) ms = t
  }
  if (ms === undefined) return undefined
  return new Date(ms).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...(timeZone ? { timeZone } : {}) })
}

/** The summary card for a type with no card of its own. */
function summaryFor(typeKey: string, c: Record<string, unknown>, title: string | undefined): NonNullable<ApprovalItem['summary']> {
  const lines: string[] = []
  let link: { url: string; label: string } | undefined
  switch (typeKey) {
    case 'skill-signoff': {
      const s = (c.signoff ?? {}) as Record<string, unknown>
      if (str(c.text)) lines.push(c.text as string)
      else for (const k of ['checked', 'tryIt', 'thenWhat']) if (str(s[k])) lines.push(s[k] as string)
      break
    }
    case 'site-change':
      if (str(c.text)) lines.push(c.text as string)
      else if (str(c.summary)) lines.push(c.summary as string)
      if (str(c.previewUrl)) link = { url: c.previewUrl as string, label: 'See your website with the change' }
      break
    case 'website':
      lines.push(str(c.domain) ? `A new website for ${c.domain as string}, ready for you to look over.` : 'Your new website, ready for you to look over.')
      if (str(c.stagingUrl)) link = { url: c.stagingUrl as string, label: 'Open the new website' }
      break
    case 'site-link-audit': {
      const findings = Array.isArray(c.findings) ? (c.findings as Array<Record<string, unknown>>) : []
      const host = str(c.siteHost) ?? str(c.siteUrl) ?? 'your website'
      lines.push(`${findings.length + (typeof c.moreCount === 'number' ? c.moreCount : 0)} link${findings.length === 1 ? '' : 's'} on ${host} not working.`)
      for (const f of findings) {
        const pages = strings(f.pages)
        if (str(f.url)) lines.push(`${f.url as string}${pages.length ? ` — on ${pages.join(', ')}` : ''}`)
      }
      if (str(c.siteUrl)) link = { url: c.siteUrl as string, label: 'Open your website' }
      break
    }
    case 'opt-in-invite-campaign':
      if (str(c.subject)) lines.push(`Subject: ${c.subject as string}`)
      if (str(c.body)) lines.push(c.body as string)
      if (typeof c.recipientCount === 'number') lines.push(`Goes to ${c.recipientCount} customer${c.recipientCount === 1 ? '' : 's'}.`)
      if (str(c.consentUrl)) link = { url: c.consentUrl as string, label: 'The page they sign up on' }
      break
    case 'quarterly-promo-plan': {
      const entries = Array.isArray(c.entries) ? (c.entries as Array<Record<string, unknown>>) : []
      for (const e of entries) {
        const head = [str(e.weekStartDate) ? `Week of ${e.weekStartDate as string}` : undefined, str(e.theme)].filter(Boolean).join(' — ')
        lines.push([head, str(e.promoIdea)].filter(Boolean).join(': '))
      }
      break
    }
    case 'connected-account-send': {
      const to = strings(c.to)
      if (to.length) lines.push(`To: ${to.join(', ')}`)
      if (str(c.subject)) lines.push(`Subject: ${c.subject as string}`)
      if (str(c.textBody)) lines.push(c.textBody as string)
      else if (str(c.htmlBody)) lines.push(blogHtmlToMarkdown(c.htmlBody as string))
      break
    }
    case 'social-profile':
      if (str(c.bio)) lines.push(c.bio as string)
      break
    case 'gbp-qa':
      if (str(c.questionText)) lines.push(`Question: ${c.questionText as string}`)
      if (str(c.answerText)) lines.push(c.answerText as string)
      break
    default:
      // Retired Story pieces (demo fixture): the piece's title + why it was made.
      if (str(c.title) ?? title) lines.push((str(c.title) ?? title) as string)
      if (str(c.rationale)) lines.push(c.rationale as string)
  }
  const images = [str(c.bannerUrl), str(c.profileImageUrl)].filter((x): x is string => !!x)
  return { lines: lines.filter((l) => l.trim().length > 0), ...(link ? { link } : {}), ...(images.length ? { images } : {}) }
}

/**
 * PURE — the preview item for one stored approval. `idx` is the list position (the
 * pop-up's numeric id). The Firestore id travels as `_firestoreId`.
 */
export function approvalDocToItem(
  d: PendingApprovalDoc & { videoNotes?: Array<{ status?: string; version?: number; timestampSec: number; text: string }>; videoVersion?: number },
  idx: number,
  opts: { timeZone?: string } = {},
): ApprovalItem & { _firestoreId?: string } {
  const c = (d.content ?? {}) as Record<string, unknown>
  // Real docs: agentId/type/title. Older/mock docs: agentType/deliverableType. Support both.
  const agentKey = d.agentId ?? d.agentType ?? ''
  const typeKey = d.type ?? d.deliverableType ?? ''
  const duration = readApprovalVideoDuration(c)
  const title = d.title ?? str(c.title)
  const fields = readApprovalContent(c)
  const isSummary = SUMMARY_TYPES.has(typeKey)
  const isEmail = typeKey === 'nurture' && c.channel === 'email'
  // A carousel stores every slide; the first is also `imageUrl`.
  const slides = Array.isArray(c.slides)
    ? (c.slides as unknown[]).map((s) => (typeof s === 'string' ? s : str((s as { url?: unknown })?.url))).filter((x): x is string => !!x)
    : []
  let platforms = readApprovalPlatforms(c)
  if (platforms.length === 0 && GOOGLE_TYPES.has(typeKey)) platforms = ['Google Business Profile']

  const type: ApprovalItem['type'] = typeKey === 'blog-post' ? 'blog'
    : isSummary ? 'summary'
      : /^(comment|dm)-response$/.test(typeKey) ? (typeKey as 'comment-response' | 'dm-response')
        : (contentTypeToFilter(typeKey) as ApprovalItem['type'])

  return {
    id: idx + 1,
    _firestoreId: d.id,
    agent: AGENT_TYPE_TO_NAME[agentKey] ?? (agentKey || 'Agent'),
    type,
    ...(typeKey === 'blog-post' ? {
      blog: { title: title ?? '', body: blogHtmlToMarkdown(fields.blogHtml ?? ''), metaDescription: fields.blogMetaDescription },
    } : {}),
    ...(isSummary ? { summary: summaryFor(typeKey, c, title), summaryTitle: title } : {}),
    platforms,
    // Main visible text: the writer's own words (text / postText / caption), else the
    // older `content`, else the title.
    content: fields.postText ?? str(c.content) ?? title ?? undefined,
    headline: str(c.headline) ?? undefined,
    proposedResponse: fields.replyText,
    message: fields.message,
    ...(isEmail ? { channel: 'email' as const, emailSubject: str(c.subject) } : typeKey === 'nurture' ? { channel: 'sms' as const } : {}),
    image: fields.image,
    ...(slides.length > 1 ? { images: slides } : {}),
    hashtags: strings(c.hashtags).join(' ') || (str(c.hashtags) ?? undefined),
    thumbnail: fields.thumbnail,
    videoUrl: fields.videoUrl,
    // ⚠️ "SCHEDULED" ONLY WHEN THERE IS A SCHEDULE (audit E13, 2026-10-28). createPendingApproval
    // stamps scheduledFor = created + 24h on EVERY approval (a placeholder, labelled there), so a
    // skill sign-off read "Scheduled for Sat, Sep 26". A real date is the writer's own
    // `scheduledDate` or a slotted item (`metadata.publishAtSlot`) — the backend publish-state
    // copy draws the same line (approval/publishStateCopy.ts).
    scheduledDate: fields.scheduledDate
      ?? ((d.metadata as { publishAtSlot?: unknown } | undefined)?.publishAtSlot === true ? formatApprovalDate(d.scheduledFor, opts.timeZone) : undefined)
      ?? '',
    contentType: str(c.contentType) ?? (c.format === 'long' ? 'video-longform' : undefined) ?? typeKey ?? 'post',
    reviewerName: fields.replyToName,
    reviewerRating: readApprovalRating(c),
    reviewText: fields.replyToText,
    recipientName: fields.recipientName,
    recipientBusiness: fields.recipientBusiness,
    lastService: fields.lastService,
    description: str(c.description) ?? str(c.rationale) ?? undefined,
    cta: fields.adCta,
    adBudgetLine: fields.adBudgetLine,
    adAudience: fields.adAudience,
    adWrittenFor: fields.adWrittenFor,
    adDestination: fields.adDestination,
    ...(c.adPlatform === 'google_search' ? {
      adPlatform: 'google-search',
      adHeadlines: strings(c.headlines),
      adDescriptions: strings(c.descriptions),
      adDisplayUrl: str(c.displayUrl),
      adKeywords: strings(c.keywords),
    } : {}),
    videoTitle: str(c.videoTitle) ?? (type === 'video' ? title ?? str(c.topic) ?? str(c.captionText) : undefined) ?? (duration ? title : undefined),
    videoDuration: duration ?? undefined,
    // The caption a Short is posted with (long-form clips store it as captionText) — the client
    // approves the words too, so the preview shows them (audit E13).
    videoCaption: str(c.captionText) ?? undefined,
    videoTags: strings(c.tags).length ? strings(c.tags) : c.videoTags ? (c.videoTags as string[]) : undefined,
    videoComments: (d.videoNotes ?? []).filter((n) => n.status === 'open' && n.version === (d.videoVersion ?? 1))
      .map((n) => ({ at: `${Math.floor(n.timestampSec / 60)}:${String(Math.floor(n.timestampSec % 60)).padStart(2, '0')}`, note: n.text }))
      .concat((c.videoComments as Array<{ at: string; note: string }> | undefined) ?? []),
    history: [],
  }
}
